import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  Sparkles,
  ArrowRight,
  Plane,
  Map,
  Compass,
  Hotel,
  Utensils,
  Camera,
  Sun,
  Mountain,
  Clock,
  Heart,
  Star,
  Users,
  Calendar,
  CheckCircle2
} from "lucide-react";

const TravelSalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Travel & Hospitality";
  }, []);

  const travelFeatures = [
    {
      icon: Heart,
      title: "Guest preference profiles",
      description: "Seat preferences, meal choices, room types, accessibility needs - all captured before arrival"
    },
    {
      icon: Hotel,
      title: "Stay customization",
      description: "Pillow types, room temperature, minibar preferences, checkout times"
    },
    {
      icon: Map,
      title: "Local recommendations",
      description: "Personalized restaurant, activity, and experience suggestions based on guest interests"
    },
    {
      icon: Users,
      title: "Loyalty & rewards",
      description: "Track guest preferences across visits for personalized loyalty experiences"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
              <Plane className="w-3 h-3 mr-1" />
              Travel
              <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-600 border-amber-200">
                Coming Soon
              </Badge>
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <EditableSection sectionId="travel-hero" label="Travel Hero" className={editMode ? "relative overflow-hidden py-20 px-4" : "relative overflow-hidden py-20 px-4"}>
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-sky-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-blue-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-amber-100 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3 mr-1" />
            <EditableText sectionId="travel-hero" field="badge" defaultValue="Coming Soon - Join the waitlist" className="inline" />
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500 bg-clip-text text-transparent">
              <EditableText sectionId="travel-hero" field="title_highlight" defaultValue="travel" className="inline" />
            </span>{" "}
            <EditableText sectionId="travel-hero" field="title_line1" defaultValue="preferences," className="inline" />
            <br />
            <EditableText sectionId="travel-hero" field="title_line2" defaultValue="perfectly communicated." className="inline" />
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            <EditableText 
              sectionId="travel-hero" 
              field="subtitle" 
              defaultValue="Know your travelers' preferences before they book. From room temperature to adventure level - create personalized experiences."
              multiline
            />
          </p>
          
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-sky-600 hover:bg-sky-700 text-white h-12 px-8 rounded-full"
            data-testid="hero-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </EditableSection>

      {/* Features Preview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            What we're building
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Help us shape the future of travel booking. Use your existing systems or go all-in with phygital.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {travelFeatures.map((feature, idx) => (
              <Card key={idx} className="border-sky-100 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-sky-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-sky-600 to-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Be first in line
          </h2>
          <p className="text-sky-100 text-lg mb-8">
            Get early access when we launch travel services.
          </p>
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-white text-sky-700 hover:bg-sky-50 h-12 px-8 rounded-full font-semibold"
            data-testid="cta-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="travel" /> */}

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="travel"
      />
    </div>
  );
};

export default TravelSalesPage;
